/**
 * "Erwartete Umsatzverteilung" — the selected month's expected amount, split.
 *
 * THE TITLE SAYS "ERWARTETE" ON PURPOSE.
 * Every figure in this card comes from `tenancy_revenue` — Vantio's contractual
 * Soll side, which reads no Bexio table at all. The design reference's plain
 * "Umsatzverteilung" would read as actual turnover, and mixing a Bexio actual
 * into this ring would put two different questions in one circle.
 *
 * `expected_recurring_other` is a backend field, not a subtraction performed
 * here. Two identities hold exactly, in Decimal, on the server:
 *
 *     recurring_rent + recurring_other == recurring_total
 *     recurring_total + one_time       == expected_total
 *
 * THE ONE DIVISION IN THIS FEATURE LIVES HERE, AND IT IS PRESENTATION ONLY.
 * A segment's percentage is its share of `expected_total` — the same number
 * printed in the middle of the ring — so the reader can see that the three
 * slices account for the whole. It is not a KPI, it is not compared against
 * anything, it is not stored, and no other file computes a percentage: KPI
 * changes come from `comparison`, which the backend already divided.
 *
 * When the total is zero there is no division at all — an empty state replaces
 * the ring rather than a set of NaN or 0 % labels.
 */
import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";

import {
  fmtMoney,
  periodLabel,
  SERIES_COLOR,
  toChartNumber,
} from "./revenueAnalyticsFormat";

const SEGMENTS = [
  { key: "expected_recurring_rent", name: "Miete (erwartet)", color: SERIES_COLOR.expected },
  {
    key: "expected_recurring_other",
    name: "Übrige wiederkehrende",
    color: SERIES_COLOR.recurringOther,
  },
  { key: "expected_one_time", name: "Einmalige Gebühren", color: SERIES_COLOR.oneTime },
];

export default function RevenueDistributionChart({ distribution, selectedPeriod }) {
  const totalRaw = distribution?.expected_total;
  const total = toChartNumber(totalRaw);

  const segments = SEGMENTS.map((s) => ({
    ...s,
    raw: distribution?.[s.key],
    value: toChartNumber(distribution?.[s.key]),
  }));

  const hasAmount = total > 0;

  return (
    <div className="flex h-full flex-col rounded-xl border border-border bg-card p-4">
      <h2 className="text-sm font-semibold text-foreground">
        Erwartete Umsatzverteilung ({periodLabel(selectedPeriod)})
      </h2>

      {!hasAmount ? (
        <p className="mt-6 text-sm text-muted-foreground">
          Für diesen Monat sind keine erwarteten Beträge hinterlegt.
        </p>
      ) : (
        <div className="mt-2 flex flex-col items-center gap-3 sm:flex-row">
          <div className="relative h-[180px] w-[180px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={segments}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={58}
                  outerRadius={84}
                  paddingAngle={1}
                  stroke="none"
                  isAnimationActive={false}
                >
                  {segments.map((s) => (
                    <Cell key={s.key} fill={s.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-[11px] text-muted-foreground">Total</span>
              <span className="text-sm font-bold text-foreground">{fmtMoney(totalRaw)}</span>
            </div>
          </div>

          <ul className="min-w-0 flex-1 list-none space-y-2 p-0 text-xs">
            {segments.map((s) => {
              // Presentation-only share of the backend's own total. Guarded by
              // `hasAmount` above, so this never divides by zero.
              const share = (s.value / total) * 100;
              return (
                <li key={s.key} className="flex items-start gap-2">
                  <span
                    aria-hidden="true"
                    className="mt-1 h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: s.color }}
                  />
                  <span className="min-w-0">
                    <span className="block text-muted-foreground">{s.name}</span>
                    <span className="block font-medium text-foreground">
                      {fmtMoney(s.raw)}{" "}
                      <span className="font-normal text-muted-foreground">
                        ({share.toLocaleString("de-CH", {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })}{" "}
                        %)
                      </span>
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* The middle figure of the contract: broader than rent, narrower than the
          total. Without it the ring shows a "Miete" slice and a "Total" centre
          with an unexplained gap between them. */}
      <p className="mt-3 border-t border-border pt-2 text-xs text-muted-foreground">
        Wiederkehrend gesamt: {fmtMoney(distribution?.expected_recurring_total)}
      </p>
    </div>
  );
}
